import React, { useState, useEffect, useCallback } from "react";
import { Input, Button, Space, Popconfirm, message, Switch, Modal, Tag, Spin } from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, AppstoreOutlined,
  SearchOutlined, CalendarOutlined
} from "@ant-design/icons";
import CategoryServ from "../../service/CategoryServ";
import AddCategory from "../../component/AddCategory";
import "../../css/category.css";
import Breadcrumbs from "../../component/Breadcrumbs";
import { formatDate } from "../../util/dateUtils";
import EmptyState from "../../util/EmptyState";
import { ResetButton } from "../../component/ui/ActionButtons";
import ShopTableImage from "../../component/ui/ShopTableImage";
import DataTable from "../../component/ui/DataTable";
import { useAdminTable } from "../hooks/useAdminTable";
import { getImageUrl as getNormalizedImageUrl } from "../../component/SafeImage";
import SafeImage from "../../component/SafeImage";

const AsyncCategoryImage = ({ record }) => {
  const [imgUrl, setImgUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const fetchImage = async () => {
      try {
        const res = await CategoryServ.single(record._id || record.id);
        if (isMounted) setImgUrl(res?.data?.imageUrl);
      } catch (error) {
        console.error("Failed to fetch category image");
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    if (!record.imageUrl) fetchImage();
    else { setImgUrl(record.imageUrl); setLoading(false); }

    return () => { isMounted = false; };
  }, [record]);

  if (loading) {
    return (
      <div style={{
        width: 52,
        height: 52,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8fafc',
        borderRadius: 10,
        border: '1px solid #e2e8f0'
      }}>
        <Spin size="small" />
      </div>
    );
  }

  return <ShopTableImage src={getNormalizedImageUrl(imgUrl)} alt="category" size={52} radius={10} />;
};

const Category = () => {
  const [searchText, setSearchText] = useState("");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCategory, setTotalCategory] = useState(0);

  const [addModal, setAddModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [details, setDetails] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [descExpanded, setDescExpanded] = useState(false);

  const fetchCategories = useCallback(async (p, sText, size) => {
    try {
      setLoading(true);
      const filterObj = { isLive: "all", parentId: null };
      if (sText) filterObj.name = sText;
      const response = await CategoryServ.index({
        limit: size,
        skip: (p - 1) * size,
        sort: JSON.stringify({ updatedAt: -1 }),
        filter: JSON.stringify(filterObj),
      });
      setCategories(response?.data || []);
      setTotalCategory(response?.total || 0);
    } catch (error) {
      console.error("Error fetching categories:", error);
      message.error("Failed to fetch categories");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => { 
      fetchCategories(page, searchText, pageSize); 
    }, 300);
    return () => clearTimeout(handler);
  }, [page, searchText, pageSize, fetchCategories]);

  const handleSearch = useCallback((value) => {
    setSearchText(value);
    setPage(1);
  }, []);

  const refresh = useCallback(() => {
    fetchCategories(page, searchText, pageSize);
  }, [fetchCategories, page, searchText, pageSize]);


  const handleAddCategory = () => { setDetails(null); setIsEditing(false); setAddModal(true); };
  const handleEdit = (record) => { setDetails(record); setIsEditing(true); setAddModal(true); setSelectedRecord(null); };
  const handleDelete = async (record) => {
    const id = record._id || record.id;
    try {
      await CategoryServ.remove(id);
      message.success("Category removed successfully");
      setCategories(prev => prev.filter(c => (c._id || c.id) !== id));
      setSelectedRecord(null);
      refresh(true);
    } catch (error) {
      message.error(error.message || "Failed to remove category");
    }
  };

  const refreshCategory = (isRefresh) => { setAddModal(false); if (isRefresh) refresh(); };

  const handleToggleLive = async (record, checked, e) => {
    if (e) e.stopPropagation();
    try {
      await CategoryServ.update(record._id || record.id, { isLive: checked });
      message.success(`Category ${checked ? "is now live" : "is no longer live"}`);
      await refresh();
      if (selectedRecord && (selectedRecord._id || selectedRecord.id) === (record._id || record.id)) {
        setSelectedRecord(prev => ({ ...prev, isLive: checked }));
      }
    } catch (error) {
      message.error("Failed to update status");
    }
  };

  // ── Table Columns ───────────────────────────────────────────
  const columns = [
    {
      title: "Image",
      dataIndex: "imageUrl",
      key: "imageUrl",
      width: 80,
      align: "center",
      render: (_, record) => <AsyncCategoryImage record={record} />,
    },
    {
      title: "Category Name",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span style={{ fontWeight: 700, color: "#1e293b", fontSize: "13px" }}>{name}</span>
          <span style={{ fontSize: "10px", color: "#94a3b8", marginTop: 2 }}>
            ID: {(record._id || record.id)?.toString().slice(-10).toUpperCase()}
          </span>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "isLive",
      key: "isLive",
      width: 150,
      align: "center",
      render: (isLive) => (
        <span style={{
          display: "inline-flex",
          alignItems: "center",
          padding: "4px 14px",
          borderRadius: 20,
          background: isLive ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.08)",
          border: `1.5px solid ${isLive ? "#86efac" : "#fca5a5"}`,
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: "0.5px",
          color: isLive ? "#16a34a" : "#dc2626",
          textTransform: "uppercase",
        }}>
          {isLive ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      title: "Returnable",
      dataIndex: "allowReturn",
      key: "allowReturn",
      width: 130,
      align: "center",
      render: (allowReturn) => {
        const isReturnable = allowReturn !== false; // defaults to true
        return (
          <span style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "4px 14px",
            borderRadius: 20,
            background: isReturnable ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.08)",
            border: `1.5px solid ${isReturnable ? "#86efac" : "#fca5a5"}`,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.5px",
            color: isReturnable ? "#16a34a" : "#dc2626",
            textTransform: "uppercase",
          }}>
            {isReturnable ? "Yes" : "No"}
          </span>
        );
      },
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 140,
      render: (date) => (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <CalendarOutlined style={{ color: "#94a3b8", fontSize: 12 }} />
          <span style={{ fontSize: "12px", color: "#64748b", fontWeight: 500 }}>
            {formatDate(date)}
          </span>
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: 100,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => { setSelectedRecord(record); setDescExpanded(false); }}
          style={{
            borderRadius: 3,
            fontWeight: 600,
            background: '#ffffffff',
            color: '#004754ff',
            border: '1px solid #e4e4e7',
            boxShadow: '0 0 0 1px #e4e4e7',
            height: 'auto',
            padding: '0px 8px',
            fontSize: 13,
          }}
        >
          View
        </Button>
      ),
    },
  ];
  const topRef = React.useRef(null);

  return (
    <div className="pcu-padding-24" ref={topRef}>
      <div className="pcu-margin-bottom-16"><Breadcrumbs /></div>

      <div className="categories-container" style={{ padding: "24px", background: "white", borderRadius: "16px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
        {/* ── Header ── */}
        <div style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <div style={{
              width: "48px", height: "48px", background: "#fff", borderRadius: "12px",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)", border: "1px solid #f1f5f9",
            }}>
              <AppstoreOutlined style={{ fontSize: "22px", color: "#4776E6" }} />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: "20px", fontWeight: 800, color: "#1a1d2e", letterSpacing: "-0.5px" }}>Categories</h1>
              <p style={{ margin: 0, fontSize: "12px", color: "#9ca3af", fontWeight: 500 }}>{totalCategory} categories managed in system</p>
            </div>
          </div>
          <Button
            type="primary"
            onClick={handleAddCategory}
            style={{
              height: "40px", borderRadius: "10px",
              background: "linear-gradient(135deg, #FF8800 0%, #FF5E00 100%)",
              border: "none", padding: "0 20px", fontSize: "13px", fontWeight: 700,
              boxShadow: "0 4px 12px rgba(255,136,0,0.3)",
              display: "flex", alignItems: "center", gap: "8px",
            }}
          >
            <PlusOutlined style={{ fontSize: "16px" }} /> Add Category
          </Button>
        </div>

        {/* ── Filters ── */}
        <div style={{ marginBottom: "24px", display: "grid", gridTemplateColumns: "1fr auto", gap: "12px", alignItems: "flex-end" }}>
          <div style={{ maxWidth: "400px" }}>
            <span style={{ color: "#94a3b8", fontSize: "10px", fontWeight: 800, letterSpacing: "0.5px", textTransform: "uppercase", display: "block", marginBottom: "8px" }}>Search Categories</span>
            <div style={{ position: "relative" }}>
              <SearchOutlined style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", zIndex: 1 }} />
              <Input
                placeholder="Search by name..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ height: "40px", paddingLeft: "36px", borderRadius: "10px", border: "1px solid #e2e8f0" }}
              />
            </div>
          </div>
          <ResetButton onClick={() => setSearchText("")} text="Reset Filters" />
        </div>

        {/* ── Table ── */}
        <div className="pcu-border-top pcu-margin-x-n24">
          <DataTable
            data={categories}
            columns={columns}
            rowKey={(record) => record._id || record.id}
            locale={{
              emptyText: (
                <EmptyState
                  title="No Categories"
                  description={searchText ? "No categories match your search." : "No categories found. Add one to get started."}
                  gradient="blue"
                  onAction={!searchText ? handleAddCategory : undefined}
                  actionText="Add Category"
                  icon="inbox"
                />
              )
            }}
            loading={loading}
            pagination={{
              current: page,
              pageSize: pageSize,
              total: totalCategory,
              onShowSizeChange: (c, s) => setPageSize(s),
              onChange: (p) => {
                setPage(p);
                topRef.current?.scrollIntoView({ behavior: 'smooth' });
              },
            }}
          />
        </div>

        {/* ── Add/Edit Modal ── */}
        {addModal && (
          <AddCategory
            open={addModal}
            categoryDetails={details}
            isEditing={isEditing}
            refreshCategory={refresh}
            onClose={() => setAddModal(false)}
          />
        )}

        {/* ── Details Modal (Shop-style) ── */}
        <Modal
          open={!!selectedRecord}
          onCancel={() => setSelectedRecord(null)}
          footer={null}
          width={580}
          centered
          className="premium-modal"
          closeIcon={
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          }
        >
          {selectedRecord && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/* ── Hero: Image + Name + Status ── */}
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <div style={{ width: 72, height: 72, borderRadius: 14, overflow: "hidden", border: "1.5px solid #e0e7ff", flexShrink: 0 }}>
                  <SafeImage
                    src={selectedRecord.imageUrl}
                    alt={selectedRecord.name}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: "#111827" }}>{selectedRecord.name}</h2>
                    <Tag
                      color={selectedRecord.isLive ? "success" : "error"}
                      style={{ borderRadius: 6, fontWeight: 700, fontSize: 10, padding: "1px 8px" }}
                    >
                      {selectedRecord.isLive ? "ACTIVE" : "INACTIVE"}
                    </Tag>
                  </div>
                  <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
                    ID: {(selectedRecord._id || selectedRecord.id)?.toString().slice(-14).toUpperCase()}
                  </div>
                </div>
              </div>

              {/* ── Detail Grid ── */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {/* Description — fixed height, scrollable on expand */}
                <div style={{ gridColumn: "1 / -1", background: "#f8fafc", borderRadius: 10, padding: "10px 14px", border: "1px solid #f1f5f9" }}>
                  <div style={{ fontSize: 9, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Description</div>
                  <div style={{
                    fontSize: 13, color: "#374151", fontWeight: 500, lineHeight: 1.6,
                    maxHeight: descExpanded ? 180 : 88,
                    overflowY: descExpanded ? "auto" : "hidden",
                    transition: "max-height 0.25s ease",
                    paddingRight: descExpanded ? 4 : 0,
                  }}>
                    {selectedRecord.description || "No description provided."}
                  </div>
                  {selectedRecord.description && selectedRecord.description.length > 200 && (
                    <button
                      onClick={() => setDescExpanded(p => !p)}
                      style={{
                        marginTop: 6, background: "none", border: "none", padding: 0,
                        cursor: "pointer", fontSize: 12, fontWeight: 700, color: "#4776E6",
                        display: "flex", alignItems: "center", gap: 4,
                      }}
                    >
                      {descExpanded ? "▲ Show less" : "▼ Show more"}
                    </button>
                  )}
                </div>

                {/* Live Status (isLive) */}
                <div style={{ background: "#f8fafc", borderRadius: 10, padding: "10px 14px", border: "1px solid #f1f5f9" }}>
                  <div style={{ fontSize: 9, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Live Status</div>
                  <div
                    onClick={e => e.stopPropagation()}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 8,
                      padding: "5px 12px 5px 8px", borderRadius: 20,
                      background: selectedRecord.isLive ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.08)",
                      border: `1.5px solid ${selectedRecord.isLive ? "#86efac" : "#fca5a5"}`,
                    }}
                  >
                    <Switch
                      size="small"
                      checked={selectedRecord.isLive}
                      onChange={(checked) => handleToggleLive(selectedRecord, checked)}
                      style={{ backgroundColor: selectedRecord.isLive ? "#22c55e" : "#ef4444" }}
                    />
                    <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.5px", color: selectedRecord.isLive ? "#16a34a" : "#dc2626", textTransform: "uppercase" }}>
                      {selectedRecord.isLive ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>

                {/* Active Status (isActive) */}
                <div style={{ background: "#f8fafc", borderRadius: 10, padding: "10px 14px", border: "1px solid #f1f5f9" }}>
                  <div style={{ fontSize: 9, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Record Active</div>
                  <span style={{
                    display: "inline-flex", alignItems: "center", padding: "4px 12px",
                    borderRadius: 20, fontSize: 11, fontWeight: 800, letterSpacing: "0.5px", textTransform: "uppercase",
                    background: selectedRecord.isActive !== false ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.08)",
                    border: `1.5px solid ${selectedRecord.isActive !== false ? "#86efac" : "#fca5a5"}`,
                    color: selectedRecord.isActive !== false ? "#16a34a" : "#dc2626",
                  }}>
                    {selectedRecord.isActive !== false ? "Active" : "Removed"}
                  </span>
                </div>

                {/* Return Eligibility */}
                <div style={{ background: "#f8fafc", borderRadius: 10, padding: "10px 14px", border: "1px solid #f1f5f9" }}>
                  <div style={{ fontSize: 9, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Return Eligibility</div>
                  <span style={{
                    display: "inline-flex", alignItems: "center", padding: "4px 12px",
                    borderRadius: 20, fontSize: 11, fontWeight: 800, letterSpacing: "0.5px", textTransform: "uppercase",
                    background: selectedRecord.allowReturn !== false ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.08)",
                    border: `1.5px solid ${selectedRecord.allowReturn !== false ? "#86efac" : "#fca5a5"}`,
                    color: selectedRecord.allowReturn !== false ? "#16a34a" : "#dc2626",
                  }}>
                    {selectedRecord.allowReturn !== false ? "Returnable" : "Non-Returnable"}
                  </span>
                </div>

                {/* Product Count */}
                <div style={{ background: "#f8fafc", borderRadius: 10, padding: "10px 14px", border: "1px solid #f1f5f9" }}>
                  <div style={{ fontSize: 9, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Products</div>
                  <div style={{ fontSize: 16, color: "#4776E6", fontWeight: 800 }}>{selectedRecord.productCount || 0}</div>
                </div>

                {/* Price Handling */}
                <div style={{ background: "#f8fafc", borderRadius: 10, padding: "10px 14px", border: "1px solid #f1f5f9" }}>
                  <div style={{ fontSize: 9, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Price Handling</div>
                  <div style={{ fontSize: 13, color: "#374151", fontWeight: 600 }}>
                    {selectedRecord.priceHandling?.type
                      ? `${selectedRecord.priceHandling.type === 'percentage' ? 'Percentage' : 'Fixed'} — ${selectedRecord.priceHandling.value || 0}${selectedRecord.priceHandling.type === 'percentage' ? '%' : '₹'}`
                      : "Inherit Global"}
                  </div>
                </div>

                {/* Default Commission */}
                <div style={{ background: "#f8fafc", borderRadius: 10, padding: "10px 14px", border: "1px solid #f1f5f9" }}>
                  <div style={{ fontSize: 9, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Commission</div>
                  <div style={{ fontSize: 13, color: "#374151", fontWeight: 600 }}>
                    {selectedRecord.defaultCommissionPercentage ? `${selectedRecord.defaultCommissionPercentage}%` : "0%"}
                  </div>
                </div>

                {/* Created At */}
                <div style={{ background: "#f8fafc", borderRadius: 10, padding: "10px 14px", border: "1px solid #f1f5f9" }}>
                  <div style={{ fontSize: 9, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Created At</div>
                  <div style={{ fontSize: 13, color: "#374151", fontWeight: 600 }}>{formatDate(selectedRecord.createdAt)}</div>
                </div>

                {/* Updated At */}
                <div style={{ background: "#f8fafc", borderRadius: 10, padding: "10px 14px", border: "1px solid #f1f5f9" }}>
                  <div style={{ fontSize: 9, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Updated At</div>
                  <div style={{ fontSize: 13, color: "#374151", fontWeight: 600 }}>{formatDate(selectedRecord.updatedAt)}</div>
                </div>
              </div>

              {/* ── Footer Actions ── */}
              <div style={{ paddingTop: 14, borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", gap: 10 }}>
                  <Button
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(selectedRecord)}
                    style={{ height: 36, borderRadius: 8, color: "#4776E6", border: "1px solid #c7d2fe", background: "#eef2ff", fontWeight: 700, fontSize: 13, display: "flex", alignItems: "center", gap: 4 }}
                  >
                    Edit
                  </Button>
                  <Popconfirm
                    title="Delete Category"
                    description="Are you sure you want to remove this category?"
                    onConfirm={() => handleDelete(selectedRecord)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      icon={<DeleteOutlined />}
                      style={{ height: 36, borderRadius: 8, color: "#ef4444", border: "1px solid #fee2e2", background: "#fef2f2", fontWeight: 700, fontSize: 13, display: "flex", alignItems: "center", gap: 4 }}
                    > Remove </Button>
                  </Popconfirm>
                </div>
                <Button
                  onClick={() => setSelectedRecord(null)}
                  style={{ height: 36, borderRadius: 8, color: "#1a1d2e", border: "1px solid #e2e8f0", fontWeight: 600, fontSize: 13, padding: "0 20px" }}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default Category;
